import { Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { QuoteExplorerMode } from 'components/quote_explorer/QuoteExplorer'
import AllowanceClass from 'components/quoting/quotes/flat_allowances/AllowanceClass'
import { type Allowances, getDefaultAllowance } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { getFullName } from 'components/reusable/form/StateDropdown'
import { AllowanceTextInput } from 'pages/employer_onboarding/quote/options/AllowanceTextInput'
import { Button, SolidCircleCheckIcon } from 'slab'
import { fullStateNameFromAbbreviation } from 'utils'

import { emitEmployerOnboardingEvent } from '../../utils'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'

export function FlatQuoteOptions () {
  const { isFlatRecommended, eligibleEmployees } = useSelfServeExplorerContext()
  const { control } = useFormContext<Allowances>()

  const state = useMemo(() => {
    const stateCode = eligibleEmployees[0]?.rating_area_code?.substring(0, 2)
    return stateCode ? fullStateNameFromAbbreviation(stateCode) : null
  }, [eligibleEmployees])

  const { fields: allowanceFields, append: appendAllowance, remove: removeAllowance } = useFieldArray({
    control,
    name: 'allowances',
  })

  const handleRemoveAllowance = (index: number) => {
    removeAllowance(index)
    emitEmployerOnboardingEvent('[ER] AllowanceTool state class removed', {})
  }

  const defaultAllowanceIndex = useMemo(() => {
    return allowanceFields.findIndex(allowance => !allowance.class)
  }, [allowanceFields])

  const sortedClassAllowances = useMemo(() => (
    allowanceFields
      .map((field, index) => ({
        ...field,
        index,
      }))
      .filter(allowance => allowance.class)
      .sort((a, b) => (getFullName(a.class?.location_id || '') || 'Z').localeCompare(getFullName(b.class?.location_id || '') || 'Z'))
  ), [allowanceFields])

  const [allowanceAddedAt, setAllowanceAddedAt] = useState(0)
  const lastClassRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (allowanceAddedAt && lastClassRef.current) {
      setTimeout(() => {
        lastClassRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 0)
    }
  }, [allowanceAddedAt])

  return (
    <div className='flex flex-col gap-6 p-6'>
      {isFlatRecommended && (
        <div className='flex flex-col gap-2 mt-3'>
          <div className='text-center'>Recommended allowance type</div>
          <div className='flex flex-wrap gap-2 justify-center'>
            <span className='inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-50/60'>
              <SolidCircleCheckIcon className='w-4 h-4 text-green-400' />
              10 or fewer employees
            </span>
            {state && (
              <span className='inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-50/60'>
                <SolidCircleCheckIcon className='w-4 h-4 text-green-400' />
                All employees in {state}
              </span>
            )}
          </div>
        </div>
      )}
      <div>
        <div className='heading-sm text-gray-800'>Default allowance</div>
        <div className='body text-gray-500 mt-1 mb-4'>
          This default allowance amount is provided for new employees. You can create allowances for specific states using the "Add state allowances" link below
        </div>
        <AllowanceTextInput
          formIndex={defaultAllowanceIndex}
          loading={defaultAllowanceIndex === -1}
        />
      </div>
      <hr className='my-2 border-gray-200' />
      <div className='flex flex-col gap-2'>
        <div className='flex flex-row items-center justify-between'>
          <div className='text-base font-semibold text-gray-800'>Allowance by state</div>
          <Button
            variant='tertiary'
            leftIcon={Plus}
            color='purple'
            onClick={() => {
              appendAllowance({ class: { location_type: 'state' }, ...getDefaultAllowance() })
              setAllowanceAddedAt((prev) => prev + 1)
              emitEmployerOnboardingEvent('[ER] AllowanceTool new state class added', {})
            }}
          >
            Add state alowances
          </Button>
        </div>
        {sortedClassAllowances.length > 0 &&
          (
            sortedClassAllowances.map((allowance, idx) => (
              <div
                key={allowanceFields[allowance.index].id}
                ref={idx === sortedClassAllowances.length - 1 ? lastClassRef : undefined}
              >
                <AllowanceClass
                  index={allowance.index}
                  defaultOpen={!allowance?.class?.location_id}
                  onAdd={() => {}}
                  onRemove={() => handleRemoveAllowance(allowance.index)}
                  mode={QuoteExplorerMode.edit}
                />
              </div>
            ))
          )}
      </div>
    </div>
  )
}
